import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useScrollPosition, useAuth } from "@/hooks";
import { getOptimizedImageUrl } from "@/lib/cloudinary";
import { siteSettingsService } from "@/services/firebase";
import { cn } from "@/lib/utils";

const navLinks = [
  { label: "Accueil", href: "#" },
  { label: "Le constat", href: "#problem" },
  { label: "Notre solution", href: "#solution" },
  { label: "Comment ça marche", href: "#how-it-works" },
  { label: "Nos artisans", href: "#services" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export function Navbar() {
  const { isScrolled } = useScrollPosition();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logo, setLogo] = useState<string | undefined>();
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    const unsubscribe = siteSettingsService.subscribe((settings) => {
      setLogo(settings?.logo);
    });

    return () => unsubscribe();
  }, []);

  const handleClick = (href: string) => {
    setMobileOpen(false);
    if (href === "#") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          isScrolled
            ? "bg-white/95 backdrop-blur-lg shadow-soft"
            : "bg-transparent"
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
              <Link to="/" className="flex items-center gap-2 group">
                <img 
                  src={logo ? getOptimizedImageUrl(logo, ["w_80", "h_80", "c_fit"]) : "/logo.png"}
                  alt="Logo Allo Artisan" 
                  className="w-10 h-10 object-contain rounded-lg"
                />
                <div className="flex flex-col">
                  <span className={cn(
                    "font-bold text-lg leading-tight transition-colors",
                    isScrolled ? "text-dark" : "text-white"
                  )}>
                    Allo Artisan
                  </span>
                  <span className={cn(
                    "text-[10px] leading-tight transition-colors",
                    isScrolled ? "text-gray-500" : "text-white/70"
                  )}>
                    L'artisan de confiance
                  </span>
                </div>
              </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleClick(link.href)}
                  className={cn(
                    "px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200",
                    isScrolled
                      ? "text-gray-600 hover:text-primary hover:bg-primary/5"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                >
                  {link.label}
                </button>
              ))}
              {isAdmin && (
                <Link
                  to="/admin"
                  className={cn(
                    "ml-2 px-4 py-2 text-sm font-medium rounded-lg transition-all",
                    isScrolled
                      ? "bg-primary text-white hover:bg-primary-dark"
                      : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
                  )}
                >
                  Dashboard
                </Link>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={cn(
                "lg:hidden p-2 rounded-lg transition-colors",
                isScrolled ? "text-dark hover:bg-gray-100" : "text-white hover:bg-white/10"
              )}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-dark/95 backdrop-blur-xl cursor-pointer" onClick={() => setMobileOpen(false)} />
            <motion.nav
              initial={{ x: 100 }}
              animate={{ x: 0 }}
              exit={{ x: 100 }}
              className="absolute right-0 top-0 bottom-0 w-80 bg-dark p-6 pt-20"
            >
              <div className="flex flex-col gap-2">
                {navLinks.map((link, i) => (
                  <motion.button
                    key={link.href}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => handleClick(link.href)}
                    className="text-left px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors text-lg font-medium"
                  >
                    {link.label}
                  </motion.button>
                ))}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <button
                    onClick={() => { handleClick("#contact"); }}
                    className="w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
                  >
                    Je suis Artisan et Je rejoins
                  </button>
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
